/**
 * WalletBalanceWidget — small header badge showing the clinic's notification
 * credit balance. Mirrors BellIcon's self-contained pattern: owns its own
 * modal-open state, mounted beside the bell in AppHeader.
 *
 * Click opens the buy-credits flow (BuyCreditsModal itself decides whether
 * the signed-in user is allowed to actually place an order).
 */
import React, { useState } from "react";

import { useWalletBalance } from "../../hooks/useWalletBalance";
import { formatPaiseAsCredits } from "../../utils/billing";
import { BuyCreditsModal } from "./BuyCreditsModal";

interface WalletBalanceWidgetProps {
  /** Apply when the surrounding layout collapses (sidebar tucked). */
  compact?: boolean;
}

export const WalletBalanceWidget: React.FC<WalletBalanceWidgetProps> = ({ compact = false }) => {
  const { balancePaise, gateState, loading, refetch } = useWalletBalance();
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  // Nothing to show yet (unauthenticated / still resolving first paint) —
  // same fail-quiet behavior GlobalBillingBanners uses while data is unknown.
  if (loading || balancePaise === null) return null;

  const toneClasses =
    gateState === "zero"
      ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
      : gateState === "low"
        ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
        : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100";

  const title =
    gateState === "zero"
      ? "Out of notification credits — click to buy more"
      : gateState === "low"
        ? "Notification credits running low — click to buy more"
        : "Notification credit balance — click to buy more";

  return (
    <>
      <button
        type="button"
        onClick={() => setShowBuyCredits(true)}
        title={title}
        aria-label={title}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 min-h-[36px] text-xs font-semibold transition-colors ${toneClasses}`}
      >
        <svg
          width={compact ? 13 : 14}
          height={compact ? 13 : 14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="2" y="6" width="20" height="13" rx="2" />
          <path d="M2 10h20" />
          <circle cx="16" cy="14.5" r="1.5" fill="currentColor" stroke="none" />
        </svg>
        {formatPaiseAsCredits(balancePaise)}
      </button>

      {showBuyCredits && (
        <BuyCreditsModal onClose={() => setShowBuyCredits(false)} onPurchased={refetch} />
      )}
    </>
  );
};

export default WalletBalanceWidget;
